import { QUERY, useQueryOperation } from 'api/Query';
import { type JSX, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { HelpIcon } from 'ts/commons/components/HelpIcon';
import { FormGroup } from 'ts/components/Form';
import { IssueQuerySelector } from 'ts/perspectives/tests/issues/IssueQuerySelector';

/** Values provided by the IssueQueryFilter component. */
export type IssueQueryFilterValues = {
	/** The name of a stored query to use for filtering. */
	storedQuery?: string;

	/** The custom query to use for filtering. */
	customQuery?: string;
};

/**
 * Filter that allows to select an existing stored query to filter findings. The component will only work when wrapped
 * in a FormProvider.
 */
export function IssueQueryFilter({ legendText }: { legendText?: string }): JSX.Element {
	const project = useProjectId();
	const context = useFormContext<IssueQueryFilterValues>();
	const [selectedQueries, setSelectedQueries] = useState<IssueQueryFilterValues>({
		storedQuery: context.getValues('storedQuery'),
		customQuery: context.getValues('customQuery')
	});
	const update = (filter: IssueQueryFilterValues) => {
		context.setValue('storedQuery', filter.storedQuery);
		context.setValue('customQuery', filter.customQuery);
		setSelectedQueries(filter);
	};
	const { data: queries } = useQueryOperation(QUERY.getIssueQueries(project));
	// The API and the backend also support custom queries.
	// However, they were not implemented here, as this makes the layouting more complex
	// and is not required for the current use case (TS-39148)
	// In case this is ever needed, consider switching to GenericQueryContainer and re-visit the css
	return (
		<FormGroup grouped as="fieldset">
			<IssueQueryLegend legendText={legendText} />
			<IssueQuerySelector
				selectedQuery={selectedQueries.storedQuery ?? null}
				issueQueries={queries ?? []}
				onChange={query => update({ storedQuery: query ?? undefined })}
				fallbackQueryName="Select..."
			/>
		</FormGroup>
	);
}

function IssueQueryLegend({ legendText }: { legendText?: string }) {
	if (!legendText) {
		return null;
	}

	return (
		<legend>
			Issue Query{' '}
			<HelpIcon
				align="center"
				side="right"
				textOrMarkdown="If provided, only findings of files which were changed in at least one of the issues matched by the provided query are shown."
			/>
		</legend>
	);
}
